import { isPlatformBrowser } from '@angular/common';
import { AfterContentChecked, AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { routingTransition } from '@app/shared/animations/routing-transitions';
import { EnvironmentActions } from '@app/core/actions';
import * as fromEnvironment from '@app/core/reducers/environment.reducer';
import { CookiesService } from '@app/core/services/cookies.service';
import { hostnameIsFromMainDomain } from '@app/shared/helpers/location';
import { ThemeSimplified } from '@app/shared/helpers/theme';
import { Team } from '@app/shared/models/team';
import { DialogService } from '@ngneat/dialog';
import { select, Store } from '@ngrx/store';
import { GetDropboxPreviewOutput } from '@smash-sdk/transfer/ejs/01-2024';
import { GetRecordOutput } from '@smash-sdk/domain/ejs/01-2024';
import { Options, SourceInfo } from 'plyr';
import { Observable, Subscription } from 'rxjs';
import { first, take, withLatestFrom } from 'rxjs/operators';
import * as fromRoot from '@app/reducers';
import { DiscoverComponent } from '../modals/modal-discover/discover.component';
import { LayoutActions, WorkflowActions } from '@app/client/uploader/store/actions';
import * as fromUploader from '@app/client/uploader/store/reducers';
import { showDiscoverModal } from '@app/shared/helpers/discover';
import { ResolveThemeService } from '@app/core/services/resolvers/resolve-theme.service';
import { ResolveDomainService } from '@app/core/services/resolvers/resolve-domain.service';
import { Constant } from 'src/constant';

@Component({
  selector: 'app-layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    routingTransition
  ]
})
export class LayoutComponent implements OnInit, OnDestroy, AfterContentChecked, AfterViewInit {
  startAnimation = false;
  hasToken$: Observable<boolean>;
  menuDisplayed$: Observable<boolean>;
  selectedTeam$: Observable<Team>;
  fileOver$: Observable<boolean>;
  countryCode$: Observable<string>;
  countries$: Observable<any[]>;
  isPremium$: Observable<boolean>;
  step$: Observable<string>;
  selectedLanguage$: Observable<string>;
  onBoarding$: Observable<fromEnvironment.State['onBoarding']>;
  dropboxPreview$: Observable<GetDropboxPreviewOutput['dropbox']>;
  dropboxPreviewLoading$: Observable<boolean>;
  uploaderMode$: Observable<GetRecordOutput['record']['options']['transferMode']['default']>;

  vimeoPositionTop = 0;
  vimeoPositionLeft = 0;
  showVideo: boolean;
  isCookiesConsentSetByUser$: Observable<boolean>;
  playerSource: SourceInfo;
  playerOptions: Options;
  cookieDisplayed = true;
  subscriptions: Subscription[] = [];
  isDomainManaged: boolean;
  domain: GetRecordOutput['record'];
  theme: ThemeSimplified;
  themeLoading = false;

  constructor(
    private cd: ChangeDetectorRef,
    private readonly store$: Store<fromRoot.State & fromUploader.State>,
    private cookiesService: CookiesService,
    private resolveDomainService: ResolveDomainService,
    private resolveThemeService: ResolveThemeService,
    private dialog: DialogService,
    @Inject(PLATFORM_ID) private platformId: object,
  ) {
    this.hasToken$ = this.store$.select(fromRoot.getHasToken);
    this.selectedTeam$ = this.store$.select(fromRoot.getActiveTeam);
    this.menuDisplayed$ = this.store$.select(fromUploader.getMenuDisplayed);
    this.fileOver$ = this.store$.select(fromUploader.getIsFileOver);
    this.countryCode$ = this.store$.select(fromRoot.getCountryCode);
    this.isPremium$ = this.store$.select(fromRoot.getIsPremium);
    this.selectedLanguage$ = this.store$.select(fromRoot.getSelectedLanguage);
    this.countries$ = this.store$.select(fromRoot.getCountries);
    this.isCookiesConsentSetByUser$ = this.store$.select(fromRoot.getIsCookiesConsentSetByUser);
    this.onBoarding$ = this.store$.select(fromRoot.getOnBoardingState);
    this.step$ = this.store$.select(fromUploader.getStep);
    this.dropboxPreview$ = this.store$.select(fromUploader.getDropboxPreview);
    this.dropboxPreviewLoading$ = this.store$.select(fromUploader.getDropboxPreviewLoading);
    this.uploaderMode$ = this.store$.select(fromUploader.getMode);
  }

  ngOnInit() {
    if (!isPlatformBrowser(this.platformId)) { return; }

    this.isDomainManaged = hostnameIsFromMainDomain(location.hostname);
    this.checkFirstOrSecondVisit();

    this.subscriptions.push(
      this.resolveDomainService.resolve().subscribe(({ domain }) => {
        this.domain = domain;
        this.store$.dispatch(WorkflowActions.updateMode({ mode: domain.options?.transferMode?.default }));
        this.handleBackground();
      }),
    );

    if (hostnameIsFromMainDomain(location.hostname)) {
      this.themeLoading = true;
      this.subscriptions.push(
        this.resolveThemeService.resolve({ target: 'Internal' }).subscribe(({ theme }) => {
          this.themeLoading = false;
          this.theme = theme;
          this.handleBackground();
        })
      );
    }
  }

  handleBackground() {
    const background = this.theme?.teasing?.background || this.domain?.background;
    if (!background) return;
    const type = background.type;
    const url = 'url' in background ? background.url : background.sourceId;

    if (type === 'Vimeo' || type === 'Youtube' || type === 'Video') {
      this.playerSource = this.buildPlayerSource({ src: url, provider: type });
      this.playerOptions = this.buildPlayerOptions(background);
    }
  }

  checkFirstOrSecondVisit() {
    this.subscriptions.push(
      this.store$.pipe(withLatestFrom(
        this.store$.pipe(select(fromRoot.getCurrentDomainLoaded), first(domainLoaded => !!domainLoaded)),
        this.store$.select(fromRoot.getIsCookiesConsentSetByUser),
        this.store$.select(fromRoot.getOnBoardingState)
      ), take(1)).subscribe(([store, domainLoaded, cookieAccepted, onboarding]: any) => {
        if (this.isDomainManaged && domainLoaded) {
          if (onboarding.discoverProModal === 'hideForNow') {
            if (showDiscoverModal(onboarding.counter, onboarding.lastVisit)) {
              this.store$.dispatch(EnvironmentActions.OnBoarding({ onBoardingState: { discoverProModal: 'showNextVisit', counter: 0 } }));
            } else {
              this.store$.dispatch(EnvironmentActions.OnBoarding({ onBoardingState: { discoverProModal: 'hideForNow', counter: 1 } }));
            }
          }
          if (onboarding.discoverProModal === 'firstVisit') {
            this.store$.dispatch(EnvironmentActions.OnBoarding({ onBoardingState: { discoverProModal: 'showNextVisit', counter: 0 } }));
          }

          if (cookieAccepted && onboarding.discoverProModal === 'showNextVisit') {
            this.store$.dispatch(EnvironmentActions.loadCountries());
            this.store$.pipe(
              select(fromRoot.getCountriesLoaded),
              first(loaded => !!loaded),
              withLatestFrom(
                this.store$.select(fromRoot.getCountryCode),
                this.store$.select(fromRoot.getCountries),
              )).subscribe(([countriesLoaded, countryCode, countries]) => {
                this.dialog.open(DiscoverComponent, {
                  windowClass: 'discover-modal',
                  closeButton: false,
                  enableClose: false,
                  size: 'xs',
                  data: { countries, countryCode }
                });
              });
          }
        }
      })
    );
  }

  ngAfterViewInit() {
    this.cd.detectChanges();
  }

  ngAfterContentChecked() {
    this.cd.detectChanges();
  }

  isVideoBgReadyToPlay(event): void {
    if (isPlatformBrowser(this.platformId)) {
      setTimeout(() => {
        this.showVideo = true;
      }, 1000);
    }
  }

  onToggleMenu(event) {
    this.store$.dispatch(LayoutActions.ToggleMenu({ menuDisplayed: event.toggled }));
  }

  get themeBackgroundHtmlIdentifier(): string {
    return this.theme?.id ? `customization_background_${this.theme.id}` : '';
  }

  // Player
  buildPlayerSource({ src, provider }: { src: string, provider: 'Youtube' | 'Vimeo' | 'Video' }): SourceInfo {
    const source: SourceInfo = {
      sources: [{
        src,
        provider: Constant.videoProviders[provider],
      }],
      type: 'video' as Plyr.MediaType,
    };
    return source;
  }

  buildPlayerOptions(item: any): Options {
    const options: Options = {
      autoplay: true,
      clickToPlay: false,
      controls: [],
      volume: 0,
      storage: { enabled: false },
      loop: { active: true },
    };

    if (item.type === 'Youtube') {
      options.youtube = { noCookie: true, rel: 0, controls: 0, autohide: 1, showinfo: 0, iv_load_policy: 3, modestbranding: 1 };
    }

    if (item.type === 'Vimeo') {
      options.vimeo = { dnt: 1, byline: 0, title: 0, background: 1, loop: 1, autoplay: 1, muted: 1 };
    }

    return options;
  }

  onSwitchUploaderMode(mode: GetRecordOutput['record']['options']['transferMode']['default']) {
    this.store$.dispatch(WorkflowActions.updateMode({ mode }));
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
    this.subscriptions.length = 0;
  }

  onIframeReady() {
    this.startAnimation = true;
  }
}
